/**
 * Local GAM (Google Publisher Tag) mock for Squares.
 *
 * GAM will not serve creatives to localhost, and the DK native container cannot be
 * run locally — which is why every fix to the anchored footer banner (#699 → #755)
 * had to be tuned from prod device captures. This mock closes that gap: it installs
 * a `window.googletag` that satisfies the whole surface `src/utils/gpt.ts` touches,
 * so the REAL production code path — TenantContext → FooterAdvert size widening →
 * AdSlot → defineAdSlot/displayAdSlot/onSlotRenderEnded — runs unmodified, with the
 * mock standing in only for Google's ad server.
 *
 * It reproduces the two GPT behaviours the footer bug hinged on:
 *
 *   1. Size-mapping bucket resolution — buckets sorted by viewport width descending,
 *      first bucket whose viewport fits the window wins; when a mapping exists but
 *      no bucket matches, GPT falls back to the slot's full `defineSlot` size list
 *      (which is how a 728x90 got back in play on a 360px phone).
 *   2. The creative renders inside a fixed-size iframe, exactly as GPT injects it —
 *      so a creative taller than the frame GAM sized is clipped INSIDE the frame,
 *      the "sliced mid-glyph" defect no amount of our CSS can reveal.
 *
 * ### Enabling it
 *
 * Add `?gamMock=1` to the app URL (or load it via `/dk-shell.html`, which does this
 * for you). The flag persists in sessionStorage so in-app navigation keeps the mock;
 * `?gamMock=0` turns it off. `index.html` skips loading the real gpt.js when the
 * flag is present, so the mock and Google's script never fight over `googletag`.
 *
 * ### Tuning params (all optional)
 *
 *   - `gamMockSize=WxH`     — frame size GAM "chooses". Must be one of the sizes the
 *                             slot actually offered for the current viewport; an
 *                             un-offered size is refused with a console.warn, because
 *                             GAM can only fill shapes the slot request contained.
 *   - `gamMockCreative=WxH` — size the CREATIVE is built at, independent of the
 *                             frame. `gamMockSize=320x50&gamMockCreative=320x100`
 *                             reproduces the prod defect verbatim: a 100px creative
 *                             clipped inside a 50px frame.
 *   - `gamMockEmpty=1`      — no-fill: renders nothing, fires slotRenderEnded with
 *                             isEmpty=true.
 *   - `gamMockDelay=NN`     — ms between display() and the creative arriving
 *                             (default 400), because the banner's measured-height
 *                             plumbing only works if fill happens after mount.
 *   - `gamMockTall=1`       — report the fill as the T-Mobile line item, so the iOS
 *                             tall-frame rule (`utils/tallCreatives.ts`) engages exactly
 *                             as it would for the real creative.
 */

import type { SlotRenderEndedEvent } from '@/utils/gpt';
import type { GoogleAdsPlacement } from '@/hooks/useTenant';

const FLAG_PARAM = 'gamMock';
/** The real T-Mobile ids, so `?gamMockTall=1` exercises the production allow-list. */
const TALL_MOCK_CREATIVE_ID = 138572155774;
const TALL_MOCK_LINE_ITEM_ID = 7378726280;
const STORAGE_KEY = 'squares:gamMock';

export interface GamMockOptions {
  /** Frame size to force, when the slot offers it. */
  forcedFrameSize: [number, number] | null;
  /** Creative size to force inside the frame (defaults to the frame size). */
  forcedCreativeSize: [number, number] | null;
  /** Simulate GAM returning no ad. */
  empty: boolean;
  /** ms between display() and the creative arriving. */
  fillDelayMs: number;
  /** Report the fill under a line item listed in `utils/tallCreatives.ts`. */
  tall: boolean;
}

const parseSize = (raw: string | null): [number, number] | null => {
  if (!raw) return null;
  const match = /^(\d+)x(\d+)$/i.exec(raw.trim());
  if (!match) return null;
  return [Number(match[1]), Number(match[2])];
};

export const readGamMockOptions = (search: string): GamMockOptions => {
  const params = new URLSearchParams(search);
  const rawDelay = params.get('gamMockDelay');
  const delay = rawDelay === null ? NaN : Number(rawDelay);
  return {
    forcedFrameSize: parseSize(params.get('gamMockSize')),
    forcedCreativeSize: parseSize(params.get('gamMockCreative')),
    empty: params.get('gamMockEmpty') === '1',
    fillDelayMs: Number.isFinite(delay) && delay >= 0 ? delay : 400,
    tall: params.get('gamMockTall') === '1',
  };
};

/**
 * Whether the mock is on for this session. The URL param is the switch and
 * sessionStorage is the memory, so the flag survives in-app URL changes but never
 * leaks between tabs or into a fresh session.
 */
export const isGamMockEnabled = (
  search: string = typeof window === 'undefined' ? '' : window.location.search
): boolean => {
  const value = new URLSearchParams(search).get(FLAG_PARAM);
  if (typeof sessionStorage !== 'undefined') {
    if (value === '1') sessionStorage.setItem(STORAGE_KEY, '1');
    if (value === '0') sessionStorage.removeItem(STORAGE_KEY);
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  }
  return value === '1';
};

interface SizeMappingBucket {
  viewport: [number, number];
  sizes: [number, number][];
}

interface MockSlot {
  adUnitPath: string;
  sizes: [number, number][];
  divId: string;
  targeting: Record<string, string>;
  sizeMapping: SizeMappingBucket[] | null;
  getSlotElementId: () => string;
}

/**
 * GPT bucket resolution: buckets sorted by viewport width descending, the first
 * whose viewport fits the window wins. An empty `sizes` in the winning bucket means
 * "serve nothing here". No matching bucket falls back to the slot's full defineSlot
 * list — the exact behaviour that resurrected 728x90 on narrow phones.
 */
export const resolveCandidateSizes = (
  slot: Pick<MockSlot, 'sizes' | 'sizeMapping'>,
  viewportWidth: number,
  viewportHeight: number
): [number, number][] => {
  if (!slot.sizeMapping || slot.sizeMapping.length === 0) return slot.sizes;

  const sorted = [...slot.sizeMapping].sort((a, b) => b.viewport[0] - a.viewport[0]);
  const bucket = sorted.find(
    b => b.viewport[0] <= viewportWidth && b.viewport[1] <= viewportHeight
  );
  return bucket ? bucket.sizes : slot.sizes;
};

/**
 * Which offered size the mock fills with: the forced one when offered, otherwise
 * the tallest (then widest) candidate — the worst case for clipping, and the case
 * DK actually traffics (320x100 into the footer).
 */
export const pickFrameSize = (
  candidates: [number, number][],
  forced: [number, number] | null
): [number, number] | null => {
  if (candidates.length === 0) return null;
  if (forced) {
    const offered = candidates.find(([w, h]) => w === forced[0] && h === forced[1]);
    if (offered) return offered;
    console.warn(
      `[gamMock] gamMockSize=${forced[0]}x${forced[1]} is not among the sizes this slot ` +
        `offered for the current viewport (${candidates.map(s => s.join('x')).join(', ')}); ` +
        `GAM could never fill it. Falling back to the default pick.`
    );
  }
  return [...candidates].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0];
};

const creativeSrcdoc = (
  frame: [number, number],
  creative: [number, number],
  adUnitPath: string
): string => {
  const [cw, ch] = creative;
  // Full-bleed magenta creative in the T-Mobile mould, with hard TOP/BOTTOM edge
  // stripes so any clipping is visible at a glance — if you cannot see both
  // stripes, the creative is being cut.
  return `<!doctype html><html><head><style>
    html,body{margin:0;padding:0;overflow:hidden;background:transparent}
    .ad{width:${cw}px;height:${ch}px;box-sizing:border-box;position:relative;
      background:linear-gradient(90deg,#e20074,#a10053);
      font-family:Arial,Helvetica,sans-serif;color:#fff;
      display:flex;align-items:center;justify-content:center;flex-direction:column}
    .edge{position:absolute;left:0;right:0;height:6px;
      background:repeating-linear-gradient(90deg,#ffe600 0 12px,#111 12px 24px);
      color:#ffe600;font-size:8px;line-height:6px}
    .edge.top{top:0}.edge.bottom{bottom:0}
    .label{font-weight:bold;font-style:italic;font-size:${ch >= 90 ? 22 : 16}px;text-shadow:0 1px 2px rgba(0,0,0,.4)}
    .meta{font-size:10px;opacity:.85;margin-top:2px}
  </style></head><body>
    <div class="ad">
      <div class="edge top"></div>
      <div class="label">Game day is better — MOCK</div>
      <div class="meta">creative ${cw}x${ch} in frame ${frame[0]}x${frame[1]} · ${adUnitPath}</div>
      <div class="edge bottom"></div>
    </div>
  </body></html>`;
};

/**
 * Install the mock `window.googletag`. Call before anything queues GPT work; the
 * cmd queue executes immediately because the mock is born ready (`apiReady: true`).
 */
export const installGamMock = (
  options: GamMockOptions = readGamMockOptions(window.location.search)
): void => {
  const slots = new Map<string, MockSlot>();
  const renderListeners: ((event: SlotRenderEndedEvent) => void)[] = [];
  const pendingFills = new Map<string, ReturnType<typeof setTimeout>>();

  const fillSlot = (slot: MockSlot) => {
    const container = document.getElementById(slot.divId);
    if (!container) return;
    container.innerHTML = '';

    const candidates = resolveCandidateSizes(slot, window.innerWidth, window.innerHeight);
    const frame = options.empty ? null : pickFrameSize(candidates, options.forcedFrameSize);

    if (frame) {
      const creative = options.forcedCreativeSize ?? frame;
      const iframe = document.createElement('iframe');
      // Fixed-size frame exactly as GPT injects one: the creative inside cannot
      // grow it, which is what makes frame-vs-creative mismatches visible here.
      iframe.width = String(frame[0]);
      iframe.height = String(frame[1]);
      iframe.style.border = '0';
      iframe.style.display = 'block';
      iframe.setAttribute('scrolling', 'no');
      iframe.title = `mock ad ${slot.adUnitPath}`;
      iframe.srcdoc = creativeSrcdoc(frame, creative, slot.adUnitPath);
      container.appendChild(iframe);
    }

    console.info(
      `[gamMock] slotRenderEnded ${slot.divId} — ` +
        (frame ? `filled ${frame[0]}x${frame[1]}` : 'EMPTY (no fill)'),
      { targeting: slot.targeting, adUnitPath: slot.adUnitPath }
    );
    /*
     * Carry the same fields real GPT does, so `utils/adReporting` exercises its actual
     * production path here rather than a shape that only exists in the mock. The ids
     * are stable fakes keyed to the served size — enough to prove the reporter
     * forwards them, and obviously not real GAM ids to anyone reading Sentry.
     */
    const event: SlotRenderEndedEvent = {
      slot,
      isEmpty: !frame,
      size: frame,
      creativeId: frame
        ? options.tall
          ? TALL_MOCK_CREATIVE_ID
          : Number(`9${frame[0]}${frame[1]}`)
        : null,
      lineItemId: frame
        ? options.tall
          ? TALL_MOCK_LINE_ITEM_ID
          : Number(`8${frame[0]}${frame[1]}`)
        : null,
      advertiserId: frame ? 700000 : null,
      campaignId: frame ? 600000 : null,
      sourceAgnosticCreativeId: null,
      sourceAgnosticLineItemId: null,
      isBackfill: false,
    };
    renderListeners.slice().forEach(listener => listener(event));
  };

  const scheduleFill = (divId: string) => {
    const slot = slots.get(divId);
    if (!slot) return;
    const pending = pendingFills.get(divId);
    if (pending) clearTimeout(pending);
    pendingFills.set(
      divId,
      setTimeout(() => {
        pendingFills.delete(divId);
        fillSlot(slot);
      }, options.fillDelayMs)
    );
  };

  const pubads = {
    enableSingleRequest: () => {},
    collapseEmptyDivs: () => {},
    getSlots: () => [...slots.values()],
    refresh: (toRefresh: unknown[]) => {
      (toRefresh as MockSlot[]).forEach(slot => scheduleFill(slot.getSlotElementId()));
    },
    addEventListener: (event: 'slotRenderEnded', listener: (e: SlotRenderEndedEvent) => void) => {
      if (event === 'slotRenderEnded') renderListeners.push(listener);
    },
    removeEventListener: (
      event: 'slotRenderEnded',
      listener: (e: SlotRenderEndedEvent) => void
    ) => {
      const index = renderListeners.indexOf(listener);
      if (event === 'slotRenderEnded' && index !== -1) renderListeners.splice(index, 1);
    },
  };

  window.googletag = {
    apiReady: true,
    // Born ready: queued work runs synchronously, and anything pushed later too.
    cmd: {
      push: (fn: unknown) => {
        (fn as () => void)();
        return 0;
      },
    } as unknown as unknown[],
    defineSlot: (path: string, sizes: [number, number][], divId: string) => {
      const slot: MockSlot = {
        adUnitPath: path,
        sizes,
        divId,
        targeting: {},
        sizeMapping: null,
        getSlotElementId: () => divId,
      };
      slots.set(divId, slot);
      return {
        addService: () => {},
        defineSizeMapping: (mapping: unknown) => {
          slot.sizeMapping = mapping as SizeMappingBucket[];
        },
        setTargeting: (key: string, value: string) => {
          slot.targeting[key] = value;
        },
      };
    },
    pubads: () => pubads,
    // The builder just accumulates buckets; build() hands back the plain array the
    // mock's resolver understands.
    sizeMapping: () => {
      const buckets: SizeMappingBucket[] = [];
      return {
        addSize: (viewport: [number, number], sizes: [number, number][]) => {
          buckets.push({ viewport, sizes });
        },
        build: () => buckets,
      };
    },
    enableServices: () => {},
    display: (divId: string) => scheduleFill(divId),
    destroySlots: (toDestroy: unknown[]) => {
      (toDestroy as MockSlot[]).forEach(slot => {
        const divId = slot.getSlotElementId();
        const pending = pendingFills.get(divId);
        if (pending) clearTimeout(pending);
        pendingFills.delete(divId);
        slots.delete(divId);
        const container = document.getElementById(divId);
        if (container) container.innerHTML = '';
      });
    },
  };

  console.info('[gamMock] installed — real gpt.js suppressed for this session', options);
};

/**
 * STICKY_FOOTER placement exactly as saved in prod Admin when the clipping was
 * reported: 728x90 offered unconditionally, and a size mapping that narrows phones
 * to 320x50 alone starting at 375px. This is the config FooterAdvert's widening
 * logic exists to survive — injecting it (rather than a sanitised one) means a
 * local run tests the fix against the real hazard.
 */
export const GAM_MOCK_STICKY_FOOTER_PLACEMENT: GoogleAdsPlacement = {
  placementKey: 'STICKY_FOOTER',
  enabled: true,
  adUnitPath: '/mock-network/dfs/free-to-play/sticky-banner-footer',
  sizes: [
    [320, 50],
    [728, 90],
  ],
  sizeMappings: [{ viewport: [375, 0], sizes: [[320, 50]] }],
  targeting: { s1: 'dfs', s2: 'free-to-play', s3: 'sticky-banner-footer' },
};
